import type { FC, ReactNode } from 'react';
import React, { useCallback, useRef } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { BannerStateContainer } from '@confluence/banners';
import { useATLGeneralWebPanelListener } from '@confluence/web-panel-location';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';

import { scrollToElement } from './scrollToElement';

export type ScrollToElementFn = {
	(element: HTMLElement, isLivePage?: boolean): void;
};
export type DocumentScrollerProps = {
	children(options: { scrollToElement: ScrollToElementFn }): ReactNode;
};

/**
 * Use this component to get a reference to "scroller" function which is aware
 * of Confluence-specific top offsets (such as banners and top nav height) which
 * can overlay the element you want to be shown if not handled properly.
 * Use this "scroller" function instead of `HTMLElement.scrollIntoView`.
 *
 * Implementation note: I wish this was a hook, but we can't make that happen until
 * we upgrade to a version of `"unstated"` with hooks support.
 *
 * ```js
 *  import { DocumentScroller, ScrollToElementFn } from "@confluence/scroll";
 *
 *  const Content = () => {
 *    const scrollToElementRef = useRef<ScrollToElementFn>(() => void 0);
 *
 *    return (
 *      <div>
 *        <DocumentScroller>
 *          {({ scrollToElement }) => {
 *            scrollToElementRef.current = scrollToElement;
 *
 *            return null;
 *          }}
 *        </DocumentScroller>
 *        <h1 id="top">This is document top</h1>
 *        <br /><br /><br /><br /><br />
 *        <button onClick={e => scrollToElementRef.current(document.getElementById("top"))}>
 *          Click to get back to top
 *        </button>
 *      </div>
 *    )
 *  }
 * ```
 */
export const DocumentScroller: FC<DocumentScrollerProps> = ({ children }) => {
	const isNav4Enabled = useIsNav4Enabled();

	// we're using this as a ref to avoid consumers having a stale reference to `adjustedScrollToElement` function
	const topOffsetRef = useRef<number>(0);
	// TODO DISCO-2987 update scroll logic to account for atl-general web panel placement in nav4
	const { isATLGeneralWebPanelFixed, atlGeneralWebPanelHeight } = useATLGeneralWebPanelListener();

	const fixedWebPanelHeight = isATLGeneralWebPanelFixed ? atlGeneralWebPanelHeight : 0;

	const adjustedScrollToElement = useCallback(
		(element: HTMLElement, isLivePage?: boolean): void => {
			const topOffsetAdjustment = isNav4Enabled ? 0 : topOffsetRef.current;

			scrollToElement(
				element,
				-topOffsetAdjustment - fixedWebPanelHeight,
				// See https://product-fabric.atlassian.net/browse/PGXT-5451 and the associated PR
				'smooth',
				isLivePage,
			);
		},
		[fixedWebPanelHeight, isNav4Enabled],
	);

	return (
		<Subscribe to={[BannerStateContainer]}>
			{(bannerState: BannerStateContainer) => {
				topOffsetRef.current = bannerState.getTotalHeight();

				return children({ scrollToElement: adjustedScrollToElement });
			}}
		</Subscribe>
	);
};
DocumentScroller.displayName = 'DocumentScroller';
